import { useQuery } from "@tanstack/react-query"
import { ExternalLink, Paperclip, Workflow } from "lucide-react"
import type { MediaSource } from "@/entities/bot-builder"
import { TelegramHtml } from "@/entities/bot-builder"
import type { BroadcastDraftContent } from "@/entities/broadcast"
import { Thumbnail } from "@/features/edit-scenario-draft"

/** Пример значений для превью: так админ видит, куда встанут переменные. */
const SAMPLE: Record<string, string> = {
	firstName: "Анна",
	lastName: "Смирнова",
	username: "anna",
	userId: "123456789",
	languageCode: "ru",
}

function withSamples(text: string, botUsername: string): string {
	return text.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_match, name: string) =>
		name === "botUsername" ? botUsername : (SAMPLE[name] ?? `‹${name}›`),
	)
}

/** Как сообщение рассылки увидит пользователь в Telegram. */
export function BroadcastPreview({
	botUsername,
	content,
	mediaSource,
}: {
	botUsername: string
	content: BroadcastDraftContent
	mediaSource: MediaSource
}) {
	const { media, gallery } = content
	const isImage = media?.kind === "photo" || media?.kind === "sticker"
	const preview = useQuery({
		queryKey: [...mediaSource.cacheKey, media?.s3Key],
		queryFn: () => mediaSource.previewUrl(media?.s3Key ?? ""),
		enabled: !!media && isImage,
		staleTime: 30 * 60_000,
	})
	const html = withSamples(content.text, botUsername)
	const empty = !html.trim() && !media && !gallery?.length && content.buttons.length === 0

	return (
		<div
			className="overflow-hidden rounded-2xl border border-border/60 text-white"
			style={{ backgroundColor: "#0e1621" }}
		>
			<div className="flex items-center gap-2.5 px-3 py-2" style={{ backgroundColor: "#17212b" }}>
				<span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-sky-400 to-blue-600 text-sm font-semibold">
					{botUsername.charAt(0).toUpperCase()}
				</span>
				<div className="min-w-0">
					<p className="truncate text-sm leading-4 font-semibold">@{botUsername}</p>
					<p className="text-[11px] text-white/45">предпросмотр · имя «Анна» для примера</p>
				</div>
			</div>
			<div className="min-h-44 p-3">
				{empty ? (
					<p className="py-12 text-center text-sm text-white/40">Сообщение появится здесь</p>
				) : (
					<div className="max-w-[92%] space-y-1">
						{html.trim() || media || gallery?.length ? (
							<div className="overflow-hidden rounded-2xl rounded-bl-md bg-[#182533] text-[14px] leading-snug">
								{gallery?.length ? (
									<div className="grid grid-cols-2 gap-0.5">
										{gallery.map((item, index) => (
											<Thumbnail
												key={item.s3Key ?? item.telegram?.fileId ?? index}
												source={mediaSource}
												media={item}
												size="h-24 w-full"
											/>
										))}
									</div>
								) : null}
								{media ? (
									isImage && preview.data ? (
										<img src={preview.data.url} alt="" className="max-h-56 w-full object-cover" />
									) : (
										<div className="flex items-center gap-2 px-3 pt-2 text-xs text-white/70">
											<Paperclip className="h-3.5 w-3.5 shrink-0" />
											<span className="truncate">{media.fileName}</span>
										</div>
									)
								) : null}
								{html.trim() ? (
									<div className="px-3 py-1.5 break-words whitespace-pre-wrap">
										<TelegramHtml html={html} />
									</div>
								) : (
									<div className="h-2" />
								)}
							</div>
						) : null}
						{content.buttons.map((button) => (
							<div
								key={button.id}
								className="flex items-center justify-center gap-1.5 rounded-xl bg-[#1d2c3c]/90 px-2 py-2 text-[13px] font-medium"
							>
								<span className="truncate">{button.label || "Кнопка"}</span>
								{button.kind === "url" ? (
									<ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
								) : (
									<Workflow className="h-3 w-3 shrink-0 opacity-70" />
								)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
